import React, { useMemo } from "react";
import { Button, Card, Collapse } from "react-bootstrap";
import styles from "./CategoryFilter.module.scss";
import { CheckIcon, CheckMinusIcon, ChevronDownIcon, ChevronUpIcon } from "../../../../Interfaces/Icons";
import { Category } from "./CategoryFilter";
import { calculateCategoryState } from "./CategoryFilter.utils";

interface CategoryNodeProps {
    category: Category;
    selectedCategories: string[];
    isOpen: boolean;
    onCategorySelect: (category: Category) => void;
    onSubcategorySelect: (subcategoryValue: string, category: Category) => void;
    onToggleCollapse: (categoryName: string) => void;
}

/**
 * Individual category node with subcategories
 * Memoized to prevent unnecessary re-renders
 */
const CategoryNode: React.FC<CategoryNodeProps> = ({
    category,
    selectedCategories,
    isOpen,
    onCategorySelect,
    onSubcategorySelect,
    onToggleCollapse
}) => {
    // Calculate derived state
    const categoryState = useMemo(
        () => calculateCategoryState(category, selectedCategories),
        [category, selectedCategories]
    );

    const { isCategoryChecked, isPartiallySelected } = categoryState;

    return (
        <Card>
            <Card.Body>
                <div className={styles["card-header"]}>
                    <div className={styles["form-check"]}>
                        <div className={styles["form-check-wrap"]}>
                            <input
                                className={`form-check-input ${styles["form-check-input"]}`}
                                type="checkbox"
                                id={category.name}
                                checked={isCategoryChecked || isPartiallySelected}
                                onChange={() => onCategorySelect(category)}
                            />
                            <div className={`${styles["custom-icon-overlay"]} ${isPartiallySelected ? styles["check-minus"] : ''}`}>
                                {isPartiallySelected ? <CheckMinusIcon /> : <CheckIcon />}
                            </div>
                        </div>
                        <label
                            className={`form-check-label ${styles["form-check-label"]} ${styles["category-link"]}`}
                            htmlFor={category.name}
                        >
                            {category.name}
                        </label>
                    </div>
                    <Button
                        variant="link"
                        className={`p-0 border-0 ${styles["category-chevron"]} ${isOpen ? styles["open"] : ""}`}
                        onClick={() => onToggleCollapse(category.name)}
                        aria-controls={`collapse-text-${category.name}`}
                        aria-expanded={isOpen}
                        aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${category.name}`}
                        title={category.name}
                    >
                        <ChevronDownIcon />
                    </Button>
                </div>
                <div className={styles["card-container"]}>
                    <Collapse in={isOpen}>
                        <div className={styles["sub-categories"]}>
                            {category.subcategories.map((subcategory, index) => {
                                const subcategoryValue = `${category.name};${subcategory}`;
                                const isChecked = selectedCategories.includes(subcategoryValue);
                                
                                return (
                                    <div key={subcategoryValue} className={styles["form-check"]}>
                                        <div className={styles["form-check-wrap"]}>
                                            <input
                                                className={`form-check-input ${styles["form-check-input"]}`}
                                                type="checkbox"
                                                id={`${category.name}-${subcategory}`}
                                                checked={isChecked}
                                                onChange={() => onSubcategorySelect(subcategoryValue, category)}
                                            />
                                            <div className={styles["custom-icon-overlay"]}>
                                                <CheckIcon />
                                            </div>
                                        </div>
                                        <label
                                            className={`form-check-label ${styles["form-check-label"]} ${styles["category-link"]}`}
                                            htmlFor={`${category.name}-${subcategory}`}
                                        >
                                            {subcategory}
                                        </label>
                                    </div>
                                );
                            })}
                        </div>
                    </Collapse>
                </div>
            </Card.Body>
        </Card>
    );
};

export default React.memo(CategoryNode);
